import * as React from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '../../primitives/button.js';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../primitives/command.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover.js';
import { Spinner } from '../../primitives/spinner.js';
import { cn } from '../../lib/cn.js';
import type { FilterOption } from '../types.js';

export interface RelationPickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  search: (query: string, signal?: AbortSignal) => Promise<readonly FilterOption[]>;
  resolve?: (value: string) => Promise<FilterOption | null>;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /**
   * For a picker with no `<label>` pointing at it — inside a repeated row, say.
   * Without it the trigger is a combobox with no accessible name.
   */
  'aria-label'?: string;
}

/**
 * Typed lookup over a related entity — an agent, an agency, a zone. It never
 * loads the full set: the API is queried per keystroke (debounced), which is the
 * only shape that survives a table with tens of thousands of contacts.
 */
export function RelationPicker({
  value,
  onChange,
  search,
  resolve,
  placeholder = 'Cerca…',
  disabled,
  id,
  'aria-label': ariaLabel,
}: RelationPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [options, setOptions] = React.useState<readonly FilterOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState<FilterOption | null>(null);

  // A value restored from the URL arrives as a bare id; the label has to be
  // fetched or the chip reads as a UUID.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected?.value === value) return;
    if (!resolve) {
      setSelected({ value, label: value });
      return;
    }
    let cancelled = false;
    void resolve(value).then((option) => {
      if (!cancelled) setSelected(option ?? { value, label: value });
    });
    return () => {
      cancelled = true;
    };
  }, [value, resolve, selected?.value]);

  React.useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      void search(query, controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) setOptions(result);
        })
        .catch(() => {
          if (!controller.signal.aborted) setOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="secondary"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !selected && 'text-faint')}
        >
          {selected?.label ?? placeholder}
          <ChevronsUpDown className="opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        {/* The API already filtered; filtering again client-side would hide results. */}
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder={placeholder} />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-secondary text-muted">
                <Spinner />
                Ricerca…
              </div>
            ) : (
              <CommandEmpty>Nessun risultato.</CommandEmpty>
            )}
            {selected && (
              <CommandItem
                value="__clear__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="text-muted"
              >
                Rimuovi selezione
              </CommandItem>
            )}
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={() => {
                  setSelected(option);
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
